import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

const pillars = [
  {
    n: '01',
    title: 'Targeting & Intelligence',
    desc: 'We start with data. We build your ideal customer profile down to the individual level — title, company size, tech stack, buying signals — and create a target account list that\'s actually worth calling.',
    points: ['ICP Definition Workshop', 'Total Addressable Market Sizing', 'Target Account List (500–5,000 accounts)', 'Buying Signal Monitoring', 'Intent Data Integration']
  },
  {
    n: '02',
    title: 'Messaging & Positioning',
    desc: 'Your message is the most important variable in outbound. We develop messaging frameworks that speak directly to your buyer\'s pain — tested across channels and continuously refined.',
    points: ['Value Proposition Development', 'Pain-Based Messaging Framework', 'Subject Line & CTA Testing', 'Industry-Specific Variants', 'Objection Handling Scripts']
  },
  {
    n: '03',
    title: 'Multi-Channel Sequences',
    desc: 'Cold email alone isn\'t enough. We deploy coordinated outreach across email, LinkedIn, and phone — timed and sequenced to maximize response rates without burning your list.',
    points: ['Cold Email Sequences (7–12 steps)', 'LinkedIn Connection + Message Cadence', 'Phone & Voicemail Scripts', 'Cross-channel Coordination', 'Re-engagement Campaigns']
  },
  {
    n: '04',
    title: 'Tech Stack & Infrastructure',
    desc: 'Great outbound requires great infrastructure. We set up and configure the tools, domains, and automation so your system runs reliably at scale.',
    points: ['Domain Warmup & Deliverability Setup', 'Email Sending Infrastructure', 'CRM Pipeline Configuration', 'Sequence Automation (Apollo, Instantly, Clay)', 'Tracking & Attribution Setup']
  },
  {
    n: '05',
    title: 'Reporting & Optimization',
    desc: 'You can\'t improve what you don\'t measure. We track every metric that matters and run weekly optimization cycles to continuously improve results.',
    points: ['Weekly Performance Reports', 'Reply Rate & Meeting Rate Tracking', 'A/B Test Analysis', 'Monthly Strategy Reviews', 'Quarterly ICP Recalibration']
  }
];

export default function SalesSystem() {
  return (
    <div className="page">

      <section style={{ paddingBottom: 64, borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="red-line" />
          <span className="eyebrow">The Zahela Sales System</span>
          <h1 style={{ fontSize: 'clamp(40px, 5vw, 72px)', maxWidth: 760 }}>
            A repeatable machine<br />
            <span className="h1-dim">for predictable revenue.</span>
          </h1>
          <p className="lead" style={{ marginTop: 24 }}>
            The Zahela Sales System is a five-pillar outbound methodology built for B2B companies that need consistent pipeline — not random acts of prospecting.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section>
        <div className="container">
          {pillars.map((p, i) => (
            <div key={i} className="process-step" style={{ gridTemplateColumns: '100px 1fr 1.2fr', gap: 48 }}>
              <div className="process-number">{p.n}</div>
              <div>
                <h3 style={{ marginBottom: 16 }}>{p.title}</h3>
                <p style={{ color: 'var(--gray-mid)', fontSize: 15, lineHeight: 1.85 }}>{p.desc}</p>
              </div>
              <div style={{ background: 'var(--black-card)', border: '1px solid var(--border)', padding: '32px' }}>
                {p.points.map((pt, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: j < p.points.length - 1 ? 14 : 0 }}>
                    <Check size={14} style={{ color: 'var(--red)', marginTop: 3, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: 'var(--gray-light)' }}>{pt}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section style={{ borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', background: 'var(--black-soft)' }}>
        <div className="container">
          <div style={{ marginBottom: 56 }}>
            <div className="red-line" />
            <span className="eyebrow">Timeline</span>
            <h2>What the first 90 days look like.</h2>
          </div>
          <div className="grid-3">
            {[
              { period: 'Days 1–14', title: 'Discovery & Build', items: ['Onboarding & ICP Workshop', 'Target account list build', 'Domain & infrastructure setup', 'Messaging framework development'] },
              { period: 'Days 15–30', title: 'Launch', items: ['Sequences go live', 'LinkedIn outreach begins', 'First reports delivered', 'Initial A/B tests launched'] },
              { period: 'Days 31–90', title: 'Optimize & Scale', items: ['Weekly optimization cycles', 'Messaging refinement', 'Pipeline review meetings', 'Scale winning sequences'] }
            ].map((t, i) => (
              <div key={i} style={{ background: 'var(--black-card)', border: '1px solid var(--border)', padding: '40px' }}>
                <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--red)', marginBottom: 8 }}>{t.period}</div>
                <h3 style={{ marginBottom: 24, fontSize: 20 }}>{t.title}</h3>
                <ul className="service-list">
                  {t.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: 'var(--red)', padding: '80px 0' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32 }}>
          <div>
            <h2 style={{ color: 'var(--white)', marginBottom: 12 }}>Ready to deploy the system?</h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15 }}>Let's talk about your pipeline goals and whether we're a fit.</p>
          </div>
          <Link to="/contact" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'var(--white)', flexShrink: 0 }}>
            Book a Strategy Call <ArrowRight size={14} />
          </Link>
        </div>
      </section>

    </div>
  );
}
